import React, { useCallback, useEffect, useState, Suspense, lazy } from 'react';
import { LanguageProvider } from './i18n/LanguageContext';
import { ToastProvider } from './components/ui/Toast';
import { Layout } from './components/layout/Layout';
import { ErrorBoundary } from './components/ErrorBoundary';
import { PageErrorBoundary } from './components/PageErrorBoundary';
import { PageLoadingFallback } from './components/PageLoadingFallback';
import { CartProvider } from './contexts/CartContext';
import { UserProvider } from './contexts/UserContext';
import { useUser } from './contexts/UserContext';
import { BlogProvider } from './contexts/BlogContext';
import { SeoHead } from './seo/SeoHead';
import { StructuredData } from './seo/StructuredData';
// ═══════════════════════════════════════════
// LAZY PAGE IMPORTS
// Each page loads on demand. If one fails to
// import, only that page breaks — not the app.
// ═══════════════════════════════════════════
const HomePage = lazy(() => import('./pages/HomePage').then(m => ({
  default: m.HomePage
})));
const NatalChartPage = lazy(() => import('./pages/NatalChartPage').then(m => ({
  default: m.NatalChartPage
})));
const SolarReturnPage = lazy(() => import('./pages/SolarReturnPage').then(m => ({
  default: m.SolarReturnPage
})));
const CompatibilityPage = lazy(() => import('./pages/CompatibilityPage').then(m => ({
  default: m.CompatibilityPage
})));
const ComboAnalysisPage = lazy(() => import('./pages/ComboAnalysisPage').then(m => ({
  default: m.ComboAnalysisPage
})));
const ForecastTransitsPage = lazy(() => import('./pages/ForecastTransitsPage').then(m => ({
  default: m.ForecastTransitsPage
})));
const ConsultationPage = lazy(() => import('./pages/ConsultationPage').then(m => ({
  default: m.ConsultationPage
})));
const PricingPage = lazy(() => import('./pages/PricingPage').then(m => ({
  default: m.PricingPage
})));
const AstrologersCatalogPage = lazy(() => import('./pages/AstrologersCatalogPage').then(m => ({
  default: m.AstrologersCatalogPage
})));
const AstrologerProfilePage = lazy(() => import('./pages/AstrologerProfilePage').then(m => ({
  default: m.AstrologerProfilePage
})));
const InterpretationsHubPage = lazy(() => import('./pages/InterpretationsHubPage').then(m => ({
  default: m.InterpretationsHubPage
})));
const InterpretationDetailPage = lazy(() => import('./pages/InterpretationDetailPage').then(m => ({
  default: m.InterpretationDetailPage
})));
const CalendarPage = lazy(() => import('./pages/CalendarPage').then(m => ({
  default: m.CalendarPage
})));
const CalendarEventPage = lazy(() => import('./pages/CalendarEventPage').then(m => ({
  default: m.CalendarEventPage
})));
const BlogListPage = lazy(() => import('./pages/BlogListPage').then(m => ({
  default: m.BlogListPage
})));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage').then(m => ({
  default: m.BlogPostPage
})));
const UserDashboardPage = lazy(() => import('./pages/UserDashboardPage').then(m => ({
  default: m.UserDashboardPage
})));
const AstrologerDashboardPage = lazy(() => import('./pages/AstrologerDashboardPage').then(m => ({
  default: m.AstrologerDashboardPage
})));
const ForAstrologersPage = lazy(() => import('./pages/ForAstrologersPage').then(m => ({
  default: m.ForAstrologersPage
})));
const AdvertisingPage = lazy(() => import('./pages/AdvertisingPage').then(m => ({
  default: m.AdvertisingPage
})));
const ServicesMarketplacePage = lazy(() => import('./pages/ServicesMarketplacePage').then(m => ({
  default: m.ServicesMarketplacePage
})));
const FreeChartPage = lazy(() => import('./pages/FreeChartPage').then(m => ({
  default: m.FreeChartPage
})));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage').then(m => ({
  default: m.CheckoutPage
})));
const AuthPage = lazy(() => import('./pages/AuthPage').then(m => ({
  default: m.AuthPage
})));
const ProductDetailPage = lazy(() => import('./pages/ProductDetailPage').then(m => ({
  default: m.ProductDetailPage
})));
const OrderConfirmationPage = lazy(() => import('./pages/OrderConfirmationPage').then(m => ({
  default: m.OrderConfirmationPage
})));
const AdminPage = lazy(() => import('./pages/AdminPage').then(m => ({
  default: m.AdminPage
})));
const NatalChartDemoPage = lazy(() => import('./pages/NatalChartDemoPage').then(m => ({
  default: m.NatalChartDemoPage
})));
const FullNatalReportPage = lazy(() => import('./pages/FullNatalReportPage').then(m => ({
  default: m.FullNatalReportPage
})));
const ReportPreviewPage = lazy(() => import('./pages/ReportPreviewPage').then(m => ({
  default: m.ReportPreviewPage
})));
const FAQPage = lazy(() => import('./pages/FAQPage').then(m => ({
  default: m.FAQPage
})));
const AstroDailyPage = lazy(() => import('./pages/AstroDailyPage').then(m => ({
  default: m.AstroDailyPage
})));
const AboutPage = lazy(() => import('./pages/AboutPage').then(m => ({
  default: m.AboutPage
})));
const ContactPage = lazy(() => import('./pages/ContactPage').then(m => ({
  default: m.ContactPage
})));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage').then(m => ({
  default: m.PrivacyPolicyPage
})));
const TermsPage = lazy(() => import('./pages/TermsPage').then(m => ({
  default: m.TermsPage
})));
const DailyHoroscopePage = lazy(() => import('./pages/DailyHoroscopePage').then(m => ({
  default: m.DailyHoroscopePage
})));
const MoonCalendarPage = lazy(() => import('./pages/MoonCalendarPage').then(m => ({
  default: m.MoonCalendarPage
})));
const ServicePage = lazy(() => import('./pages/ServicePage').then(m => ({
  default: m.ServicePage
})));
// NotFoundPage loaded eagerly — it's the fallback, must always work
import { NotFoundPage } from './pages/NotFoundPage';
// ═══════════════════════════════════════════
// BROWSER HISTORY HOOK
// ═══════════════════════════════════════════
function getPageFromHash(): string {
  const hash = window.location.hash.replace('#/', '').replace('#', '');
  return hash || 'home';
}
function useHashRouter() {
  const [currentPage, setCurrentPageState] = useState<string>(getPageFromHash);
  const setCurrentPage = useCallback((page: string) => {
    setCurrentPageState(page);
    window.location.hash = `#/${page}`;
  }, []);
  useEffect(() => {
    const handleHashChange = () => {
      const page = getPageFromHash();
      setCurrentPageState(page);
    };
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);
  return {
    currentPage,
    setCurrentPage
  };
}
// ═══════════════════════════════════════════
// VALID PAGES SET (for 404 detection)
// ═══════════════════════════════════════════
const VALID_PAGES = new Set(['home', 'service-natal', 'service-solar', 'service-compatibility', 'service-combo', 'service-forecast', 'service-consultation', 'pricing', 'astrologers', 'astrologer-profile', 'interpretations', 'interpretation-detail', 'calendar', 'calendar-event', 'blog', 'blog-post', 'dashboard-user', 'dashboard-astrologer', 'for-astrologers', 'advertising', 'marketplace', 'free-chart', 'checkout', 'login', 'product-detail', 'order-confirmation', 'admin', 'natal-demo', 'full-report', 'report-preview', 'report-preview-natal', 'report-preview-solar', 'report-preview-compatibility', 'report-preview-combo', 'report-preview-forecast', 'report-preview-natal-premium', 'report-preview-solar-premium', 'report-preview-compatibility-premium', 'report-preview-combo-premium', 'report-preview-forecast-premium', 'faq', 'astro-daily', 'daily-horoscope', 'moon-calendar', 'about', 'contact', 'privacy', 'terms']);
// ═══════════════════════════════════════════
// PROTECTED ROUTE WRAPPER
// ═══════════════════════════════════════════
const PROTECTED_PAGES: Record<string, 'any' | 'admin' | 'astrologer'> = {
  'dashboard-user': 'any',
  'dashboard-astrologer': 'astrologer',
  admin: 'admin',
  checkout: 'any',
  'order-confirmation': 'any'
};
// ═══════════════════════════════════════════
// SAFE PAGE WRAPPER
// Wraps each page in Suspense + ErrorBoundary
// ═══════════════════════════════════════════
function SafePage({
  children,
  pageName,
  onNavigate
}: {
  children: React.ReactNode;
  pageName: string;
  onNavigate: (page: string) => void;
}) {
  return <PageErrorBoundary pageName={pageName} onNavigate={onNavigate} data-id="element-0">
      <Suspense fallback={<PageLoadingFallback data-id="element-2" />} data-id="element-1">{children}</Suspense>
    </PageErrorBoundary>;
}
// ═══════════════════════════════════════════
// APP CONTENT (ROUTING)
// ═══════════════════════════════════════════
function AppContent({
  currentPage,
  setCurrentPage
}: {
  currentPage: string;
  setCurrentPage: (p: string) => void;
}) {
  const {
    isLoggedIn,
    user
  } = useUser();
  // Scroll to top on page change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentPage]);
  // Route protection
  useEffect(() => {
    const protection = PROTECTED_PAGES[currentPage];
    if (!protection) return;
    if (!isLoggedIn) {
      setCurrentPage('login');
      return;
    }
    if (protection === 'admin' && user?.role !== 'admin') {
      setCurrentPage('home');
      return;
    }
    if (protection === 'astrologer' && user?.role !== 'astrologer' && user?.role !== 'admin') {
      setCurrentPage('dashboard-user');
      return;
    }
  }, [currentPage, isLoggedIn, user, setCurrentPage]);
  const renderPage = () => {
    // Handle parameterized report-preview routes
    if (currentPage.startsWith('report-preview')) {
      const isPremium = currentPage.endsWith('-premium');
      const baseRoute = isPremium ? currentPage.replace('-premium', '') : currentPage;
      const serviceMap: Record<string, string> = {
        'report-preview-natal': 'natal',
        'report-preview-solar': 'solar',
        'report-preview-compatibility': 'compatibility',
        'report-preview-combo': 'combo',
        'report-preview-forecast': 'forecast'
      };
      const initialService = serviceMap[baseRoute] as 'natal' | 'solar' | 'compatibility' | 'combo' | 'forecast' | undefined;
      return <SafePage pageName="report-preview" onNavigate={setCurrentPage} data-id="element-3">
          <ReportPreviewPage onNavigate={setCurrentPage} initialService={initialService} initialReportType={isPremium ? 'premium' : 'beta'} data-id="element-4" />
        </SafePage>;
    }
    // Handle parameterized blog post routes
    if (currentPage.startsWith('blog-post/')) {
      const articleId = currentPage.split('/')[1];
      return <SafePage pageName="blog-post" onNavigate={setCurrentPage} data-id="element-5">
          <BlogPostPage onNavigate={setCurrentPage} articleId={articleId} data-id="element-6" />
        </SafePage>;
    }
    // 404 for unknown pages
    if (!VALID_PAGES.has(currentPage) && !currentPage.startsWith('blog-post/')) {
      return <NotFoundPage onNavigate={setCurrentPage} data-id="element-7" />;
    }
    switch (currentPage) {
      case 'home':
        return <SafePage pageName="home" onNavigate={setCurrentPage} data-id="element-8">
            <HomePage onNavigate={setCurrentPage} data-id="element-9" />
          </SafePage>;
      case 'service-natal':
        return <SafePage pageName="service-natal" onNavigate={setCurrentPage} data-id="element-10">
            <NatalChartPage onNavigate={setCurrentPage} data-id="element-11" />
          </SafePage>;
      case 'service-solar':
        return <SafePage pageName="service-solar" onNavigate={setCurrentPage} data-id="element-12">
            <SolarReturnPage onNavigate={setCurrentPage} data-id="element-13" />
          </SafePage>;
      case 'service-compatibility':
        return <SafePage pageName="service-compatibility" onNavigate={setCurrentPage} data-id="element-14">
            <CompatibilityPage onNavigate={setCurrentPage} data-id="element-15" />
          </SafePage>;
      case 'service-combo':
        return <SafePage pageName="service-combo" onNavigate={setCurrentPage} data-id="element-16">
            <ComboAnalysisPage onNavigate={setCurrentPage} data-id="element-17" />
          </SafePage>;
      case 'service-forecast':
        return <SafePage pageName="service-forecast" onNavigate={setCurrentPage} data-id="element-18">
            <ForecastTransitsPage onNavigate={setCurrentPage} data-id="element-19" />
          </SafePage>;
      case 'service-consultation':
        return <SafePage pageName="service-consultation" onNavigate={setCurrentPage} data-id="element-20">
            <ConsultationPage onNavigate={setCurrentPage} data-id="element-21" />
          </SafePage>;
      case 'pricing':
        return <SafePage pageName="pricing" onNavigate={setCurrentPage} data-id="element-22">
            <PricingPage onNavigate={setCurrentPage} data-id="element-23" />
          </SafePage>;
      case 'astrologers':
        return <SafePage pageName="astrologers" onNavigate={setCurrentPage} data-id="element-24">
            <AstrologersCatalogPage onNavigate={setCurrentPage} data-id="element-25" />
          </SafePage>;
      case 'astrologer-profile':
        return <SafePage pageName="astrologer-profile" onNavigate={setCurrentPage} data-id="element-26">
            <AstrologerProfilePage onNavigate={setCurrentPage} data-id="element-27" />
          </SafePage>;
      case 'interpretations':
        return <SafePage pageName="interpretations" onNavigate={setCurrentPage} data-id="element-28">
            <InterpretationsHubPage onNavigate={setCurrentPage} data-id="element-29" />
          </SafePage>;
      case 'interpretation-detail':
        return <SafePage pageName="interpretation-detail" onNavigate={setCurrentPage} data-id="element-30">
            <InterpretationDetailPage onNavigate={setCurrentPage} data-id="element-31" />
          </SafePage>;
      case 'calendar':
        return <SafePage pageName="calendar" onNavigate={setCurrentPage} data-id="element-32">
            <CalendarPage onNavigate={setCurrentPage} data-id="element-33" />
          </SafePage>;
      case 'calendar-event':
        return <SafePage pageName="calendar-event" onNavigate={setCurrentPage} data-id="element-34">
            <CalendarEventPage onNavigate={setCurrentPage} data-id="element-35" />
          </SafePage>;
      case 'blog':
        return <SafePage pageName="blog" onNavigate={setCurrentPage} data-id="element-36">
            <BlogListPage onNavigate={setCurrentPage} data-id="element-37" />
          </SafePage>;
      case 'dashboard-user':
        return <SafePage pageName="dashboard-user" onNavigate={setCurrentPage} data-id="element-38">
            <UserDashboardPage onNavigate={setCurrentPage} data-id="element-39" />
          </SafePage>;
      case 'dashboard-astrologer':
        return <SafePage pageName="dashboard-astrologer" onNavigate={setCurrentPage} data-id="element-40">
            <AstrologerDashboardPage onNavigate={setCurrentPage} data-id="element-41" />
          </SafePage>;
      case 'for-astrologers':
        return <SafePage pageName="for-astrologers" onNavigate={setCurrentPage} data-id="element-42">
            <ForAstrologersPage onNavigate={setCurrentPage} data-id="element-43" />
          </SafePage>;
      case 'advertising':
        return <SafePage pageName="advertising" onNavigate={setCurrentPage} data-id="element-44">
            <AdvertisingPage onNavigate={setCurrentPage} data-id="element-45" />
          </SafePage>;
      case 'marketplace':
        return <SafePage pageName="marketplace" onNavigate={setCurrentPage} data-id="element-46">
            <ServicesMarketplacePage onNavigate={setCurrentPage} data-id="element-47" />
          </SafePage>;
      case 'free-chart':
        return <SafePage pageName="free-chart" onNavigate={setCurrentPage} data-id="element-48">
            <FreeChartPage onNavigate={setCurrentPage} data-id="element-49" />
          </SafePage>;
      case 'checkout':
        return <SafePage pageName="checkout" onNavigate={setCurrentPage} data-id="element-50">
            <CheckoutPage onNavigate={setCurrentPage} data-id="element-51" />
          </SafePage>;
      case 'login':
        return <SafePage pageName="login" onNavigate={setCurrentPage} data-id="element-52">
            <AuthPage onNavigate={setCurrentPage} data-id="element-53" />
          </SafePage>;
      case 'product-detail':
        return <SafePage pageName="product-detail" onNavigate={setCurrentPage} data-id="element-54">
            <ProductDetailPage onNavigate={setCurrentPage} data-id="element-55" />
          </SafePage>;
      case 'order-confirmation':
        return <SafePage pageName="order-confirmation" onNavigate={setCurrentPage} data-id="element-56">
            <OrderConfirmationPage onNavigate={setCurrentPage} data-id="element-57" />
          </SafePage>;
      case 'admin':
        return <SafePage pageName="admin" onNavigate={setCurrentPage} data-id="element-58">
            <AdminPage onNavigate={setCurrentPage} data-id="element-59" />
          </SafePage>;
      case 'natal-demo':
        return <SafePage pageName="natal-demo" onNavigate={setCurrentPage} data-id="element-60">
            <NatalChartDemoPage onNavigate={setCurrentPage} data-id="element-61" />
          </SafePage>;
      case 'full-report':
        return <SafePage pageName="full-report" onNavigate={setCurrentPage} data-id="element-62">
            <FullNatalReportPage onNavigate={setCurrentPage} data-id="element-63" />
          </SafePage>;
      case 'faq':
        return <SafePage pageName="faq" onNavigate={setCurrentPage} data-id="element-64">
            <FAQPage onNavigate={setCurrentPage} data-id="element-65" />
          </SafePage>;
      case 'astro-daily':
      case 'daily-horoscope':
        return <SafePage pageName="astro-daily" onNavigate={setCurrentPage} data-id="element-66">
            <AstroDailyPage onNavigate={setCurrentPage} initialTab="horoscope" data-id="element-67" />
          </SafePage>;
      case 'moon-calendar':
        return <SafePage pageName="moon-calendar" onNavigate={setCurrentPage} data-id="element-68">
            <AstroDailyPage onNavigate={setCurrentPage} initialTab="moon" data-id="element-69" />
          </SafePage>;
      case 'about':
        return <SafePage pageName="about" onNavigate={setCurrentPage} data-id="element-70">
            <AboutPage onNavigate={setCurrentPage} data-id="element-71" />
          </SafePage>;
      case 'contact':
        return <SafePage pageName="contact" onNavigate={setCurrentPage} data-id="element-72">
            <ContactPage onNavigate={setCurrentPage} data-id="element-73" />
          </SafePage>;
      case 'privacy':
        return <SafePage pageName="privacy" onNavigate={setCurrentPage} data-id="element-74">
            <PrivacyPolicyPage onNavigate={setCurrentPage} data-id="element-75" />
          </SafePage>;
      case 'terms':
        return <SafePage pageName="terms" onNavigate={setCurrentPage} data-id="element-76">
            <TermsPage onNavigate={setCurrentPage} data-id="element-77" />
          </SafePage>;
      default:
        return <NotFoundPage onNavigate={setCurrentPage} data-id="element-78" />;
    }
  };
  if (currentPage === 'admin') {
    return <>
        <SeoHead page={currentPage} data-id="element-79" />
        {renderPage()}
      </>;
  }
  return <>
      <SeoHead page={currentPage} data-id="element-80" />
      <StructuredData page={currentPage} data-id="element-81" />
      <Layout onNavigate={setCurrentPage} currentPage={currentPage} data-id="element-82">
        {renderPage()}
      </Layout>
    </>;
}
// ═══════════════════════════════════════════
// ROOT APP
// ═══════════════════════════════════════════
export function App() {
  const {
    currentPage,
    setCurrentPage
  } = useHashRouter();
  return <ErrorBoundary onNavigate={setCurrentPage} data-id="element-83">
      <LanguageProvider data-id="element-84">
        <ToastProvider data-id="element-85">
          <UserProvider data-id="element-86">
            <CartProvider data-id="element-87">
              <BlogProvider data-id="element-88">
                <AppContent currentPage={currentPage} setCurrentPage={setCurrentPage} data-id="element-89" />
              </BlogProvider>
            </CartProvider>
          </UserProvider>
        </ToastProvider>
      </LanguageProvider>
    </ErrorBoundary>;
}